import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Alert, Chip, CircularProgress, Tooltip,
  IconButton, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableHead, TableRow, TablePagination,
  InputAdornment, TextField, Avatar, Tabs, Tab, MenuItem, Grid, Card, CardContent,
  Badge, LinearProgress,
} from '@mui/material';
import {
  Apartment, CloudSync, Refresh, CheckCircle, Logout, Login as LoginIcon,
  Delete, WarningAmber, School, LocationOn, Phone, Email as EmailIcon,
  CalendarToday, Tune, ArrowBack, Search, StorageRounded, AdminPanelSettings,
  Dashboard as DashboardIcon, Payments as PaymentsIcon, People, OpenInNew,
  Edit as EditIcon, Inbox as InboxIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { locale } from '../utils/currency';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { useNavigate } from 'react-router-dom';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_COLOR = {
  PendingPayment: 'warning',
  Provisioning:   'info',
  Active:         'success',
  Suspended:      'default',
  Closed:         'error',
};

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString(locale(), { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

// Secondary table columns — hidden on phones, shown from md up. Keeps the
// essential columns visible on mobile without a wide horizontal scroll.
const hideXs = { display: { xs: 'none', md: 'table-cell' } };

const avatarColor = (status) => ({
  Active:       '#1b5e20',
  Provisioning: '#0d47a1',
  Suspended:    '#757575',
  Closed:       '#b71c1c',
  PendingPayment: '#e65100',
})[status] || '#455a64';

const SchoolsAdmin = () => {
  const navigate = useNavigate();
  const [schools,      setSchools]      = useState([]);
  const [error,        setError]        = useState('');
  const [msg,          setMsg]          = useState('');
  const [loading,      setLoading]      = useState(true);
  const [provisioning, setProvisioning] = useState(null);
  const [creatingDemo, setCreatingDemo] = useState(false);
  const NEW_SCHOOL_BLANK = { code: '', name: '', motto: '', schoolType: 'Private', level: 'Mixed', county: '', subCounty: '', phone: '', email: '', address: '' };
  const [newSchoolDlg,  setNewSchoolDlg]  = useState(false);
  const [newSchoolForm, setNewSchoolForm] = useState(NEW_SCHOOL_BLANK);
  const [creatingNew,   setCreatingNew]   = useState(false);
  const [confirmDel,   setConfirmDel]   = useState(null);
  const [manageDlg,    setManageDlg]    = useState(null);
  const [detailDlg,    setDetailDlg]    = useState(null);   // school row clicked
  const [updating,     setUpdating]     = useState(false);
  const [search,       setSearch]       = useState('');
  const [page,         setPage]         = useState(0);
  const [rowsPerPage,  setRowsPerPage]  = useState(25);
  const [tab,          setTab]          = useState(0);       // 0 = Schools, 1 = Super Admins
  const [allUsers,     setAllUsers]     = useState([]);
  const [adminSearch,  setAdminSearch]  = useState('');
  const [adminPage,    setAdminPage]    = useState(0);
  const [adminRows,    setAdminRows]    = useState(10);
  const [userFilter,   setUserFilter]   = useState('all'); // all | super | admin | user | parent | unassigned
  const [userSchool,   setUserSchool]   = useState('');    // filter users to one school (schoolId)
  const [userCounty,   setUserCounty]   = useState('');    // filter users by their school's county
  const [userSchoolType, setUserSchoolType] = useState(''); // filter by school type (Private/Public…)
  const [editUserDlg,  setEditUserDlg]  = useState(null);   // editing a user
  const [editForm,     setEditForm]     = useState({});
  const [deleteDlg,    setDeleteDlg]    = useState(null);   // confirm delete user
  const [promoDlg,     setPromoDlg]     = useState(false);  // confirm promo email blast
  const [sendingPromo, setSendingPromo] = useState(false);
  const [promoResult,  setPromoResult]  = useState(null);   // { sent, failed, total }
  const [promoCampaigns, setPromoCampaigns] = useState([]); // available campaigns
  const [promoCampaign,  setPromoCampaign]  = useState(''); // selected campaign id
  const [promoRecipients, setPromoRecipients] = useState([]); // user objects to email
  const [editSchoolDlg,  setEditSchoolDlg]  = useState(null); // editing school details
  const [editSchoolForm, setEditSchoolForm] = useState({});
  const userGuard   = useUnsavedGuard({ open: Boolean(editUserDlg),   value: editForm,       onClose: () => setEditUserDlg(null) });
  const schoolGuard = useUnsavedGuard({ open: Boolean(editSchoolDlg), value: editSchoolForm, onClose: () => setEditSchoolDlg(null) });
  const [reseedConfirm,  setReseedConfirm]  = useState(null); // confirm reseed dialog
  const [inquiries,      setInquiries]      = useState([]);    // school inquiries (for count badge)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, uRes] = await Promise.all([
        axios.get(`${API}/api/schools`, { headers: hdrs() }),
        axios.get(`${API}/api/users?all=true`, { headers: hdrs() }),
      ]);
      setSchools(sRes.data);
      setAllUsers(uRes.data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load schools');
    } finally { setLoading(false); }
    // Load inquiries separately so a failure here doesn't block the page
    try {
      const iRes = await axios.get(`${API}/api/school-inquiries`, { headers: hdrs() });
      setInquiries(iRes.data || []);
    } catch { /* non-fatal */ }
  }, []);

  const pendingInquiries = inquiries.filter(i => i.status === 'pending').length;

  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 5000); };

  const openEditUser = (user) => {
    setEditForm({
      _id: user._id,
      fullName: user.fullName,
      email: user.email,
      username: user.username,
      role: user.role,
      status: user.status,
      schoolId: user.schoolId,
      isSuperAdmin: user.isSuperAdmin,
    });
    setEditUserDlg(user);
  };

  const saveEditUser = async () => {
    setUpdating(true);
    try {
      await axios.put(`${API}/api/users/${editForm._id}`, editForm, { headers: hdrs() });
      flash('User updated');
      setEditUserDlg(null);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to update user');
    } finally { setUpdating(false); }
  };

  // Open the promo dialog for a given set of recipients, loading campaigns first.
  const openPromo = async (recipients) => {
    setPromoRecipients(recipients || []);
    setPromoResult(null);
    setPromoDlg(true);
    try {
      const { data } = await axios.get(`${API}/api/users/promo-campaigns`, { headers: hdrs() });
      setPromoCampaigns(data);
      setPromoCampaign(prev => (data.some(c => c.id === prev) ? prev : (data[0]?.id || '')));
    } catch {
      setPromoCampaigns([]);   // dialog still works; backend defaults to first campaign
    }
  };

  // Send the promo email to the chosen recipients.
  const sendPromo = async () => {
    setSendingPromo(true);
    setError('');
    try {
      const userIds = promoRecipients.map(u => u._id);
      const { data } = await axios.post(`${API}/api/users/send-promo`, { userIds, campaign: promoCampaign }, { headers: hdrs() });
      setPromoResult(data);
      flash(`Promo sent to ${data.sent} of ${data.total} user${data.total === 1 ? '' : 's'}${data.failed ? ` (${data.failed} failed)` : ''}`);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to send promotional message');
    } finally { setSendingPromo(false); }
  };

  const deleteUser = async (userId) => {
    setUpdating(true);
    try {
      await axios.delete(`${API}/api/users/${userId}`, { headers: hdrs() });
      flash('User deleted');
      setDeleteDlg(null);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to delete user');
    } finally { setUpdating(false); }
  };

  const openEditSchool = (s) => {
    setEditSchoolForm({
      code:       s.code       || '',
      name:       s.name       || '',
      motto:      s.motto      || '',
      schoolType: s.schoolType || 'Private',
      level:      s.level      || 'Mixed',
      county:     s.county     || '',
      subCounty:  s.subCounty  || '',
      phone:      s.phone      || '',
      email:      s.email      || '',
      address:    s.address    || '',
    });
    setEditSchoolDlg(s);
  };

  const saveEditSchool = async () => {
    if (!editSchoolForm.code?.trim()) { setError('School code is required'); return; }
    if (!editSchoolForm.name.trim()) { setError('School name is required'); return; }
    setUpdating(true);
    try {
      const r = await axios.put(`${API}/api/schools/${editSchoolDlg._id}`, editSchoolForm, { headers: hdrs() });
      flash(`"${r.data.name}" updated.`);
      setEditSchoolDlg(null);
      setDetailDlg(prev => prev?._id === r.data._id ? r.data : prev);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Update failed');
    } finally { setUpdating(false); }
  };

  const updateField = async (s, patch) => {
    setUpdating(true);
    try {
      const r = await axios.put(`${API}/api/schools/${s._id}`, patch, { headers: hdrs() });
      flash(`"${s.name}" updated.`);
      setManageDlg(r.data);
      // Sync detail dialog if open
      setDetailDlg(prev => prev?._id === s._id ? r.data : prev);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Update failed');
    } finally { setUpdating(false); }
  };

  const markPaid = async (s) => {
    setUpdating(true); setError('');
    try {
      await axios.post(`${API}/api/schools/${s._id}/pay-commitment`, {}, { headers: hdrs() });
      flash(`Commitment fee recorded for "${s.name}".`);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not record payment');
    } finally { setUpdating(false); }
  };

  const provision = async (s) => {
    setProvisioning(s._id); setError('');
    try {
      const r = await axios.post(`${API}/api/schools/${s._id}/provision`, {}, { headers: hdrs() });
      const total = r.data.seeded?.counts
        ? Object.values(r.data.seeded.counts).reduce((a, b) => a + b, 0) : 0;
      flash(`Provisioned "${s.name}"${total ? ` — seeded ${total} records` : ''}.`);
      await load();
      // Refresh detail dialog
      const updated = await axios.get(`${API}/api/schools`, { headers: hdrs() });
      const fresh = updated.data.find(x => x._id === s._id);
      if (fresh) setDetailDlg(fresh);
    } catch (e) {
      setError(e.response?.data?.error || 'Provisioning failed');
    } finally { setProvisioning(null); }
  };

  const handleReseedConfirm = async () => {
    const school = reseedConfirm;
    setReseedConfirm(null);
    await provision(school);
  };

  const openSchoolInApp = async (s) => {
    setError('');
    try {
      const r = await axios.post(`${API}/api/schools/${s._id}/open`, {}, { headers: hdrs() });
      const { token, school } = r.data;
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify({
        ...user, schoolId: school._id, dedicatedDb: school.dedicatedDb,
        hasSchool: true, impersonating: true, isSuperAdmin: true, role: 'admin',
      }));
      localStorage.setItem('school', JSON.stringify({
        id: school._id, code: school.code, name: school.name,
        county: school.county, provisionStatus: school.provisionStatus,
      }));
      navigate('/dashboard');
    } catch (e) {
      setError(e.response?.data?.error || 'Could not open school');
    }
  };

  const createDemo = async () => {
    setCreatingDemo(true); setError('');
    try {
      const r = await axios.post(`${API}/api/schools/demo`, {}, { headers: hdrs() });
      const total = r.data.seeded?.counts
        ? Object.values(r.data.seeded.counts).reduce((a, b) => a + b, 0) : 0;
      flash(`Demo school created & seeded (${total} records). Opening…`);
      await load();
      await openSchoolInApp(r.data.school);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not create demo school');
    } finally { setCreatingDemo(false); }
  };

  const createNewSchool = async () => {
    if (!newSchoolForm.code.trim() || !newSchoolForm.name.trim()) {
      setError('School code and name are required'); return;
    }
    setCreatingNew(true); setError('');
    try {
      const r = await axios.post(`${API}/api/schools/admin-create`, newSchoolForm, { headers: hdrs() });
      flash(`School "${r.data.school.name}" (${r.data.school.code}) created & provisioned.`);
      setNewSchoolDlg(false);
      setNewSchoolForm(NEW_SCHOOL_BLANK);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not create school');
    } finally { setCreatingNew(false); }
  };

  const deleteSchool = async () => {
    const s = confirmDel; setConfirmDel(null); setError('');
    try {
      await axios.delete(`${API}/api/schools/${s._id}`, { headers: hdrs() });
      flash(`"${s.name}" removed from registry.`);
      setDetailDlg(null);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Delete failed');
    }
  };

  const signOut = () => {
    ['token', 'user', 'school'].forEach(k => localStorage.removeItem(k));
    navigate('/login');
  };

  const filtered = schools.filter(s => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (s.name || '').toLowerCase().includes(q) ||
           (s.code || '').toLowerCase().includes(q) ||
           (s.county || '').toLowerCase().includes(q) ||
           (s.provisionStatus || '').toLowerCase().includes(q);
  });

  const paged = filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const schoolById = useMemo(() => Object.fromEntries(schools.map(s => [s._id, s])), [schools]);
  const schoolName = (id) => {
    if (!id) return null;
    const s = schoolById[id];
    return s ? `${s.name} (${s.code})` : id;
  };

  // Distinct counties / school types across the registry, for the filter dropdowns.
  const counties = useMemo(
    () => [...new Set(schools.map(s => (s.county || '').trim()).filter(Boolean))].sort(),
    [schools]);
  const schoolTypes = useMemo(
    () => [...new Set(schools.map(s => (s.schoolType || '').trim()).filter(Boolean))].sort(),
    [schools]);

  const filteredAdmins = allUsers.filter(u => {
    if (userFilter === 'super'      && !u.isSuperAdmin) return false;
    if (userFilter === 'admin'      && u.role !== 'admin') return false;
    if (userFilter === 'user'       && u.role !== 'user') return false;
    if (userFilter === 'parent'     && u.role !== 'parent') return false;
    if (userFilter === 'unassigned' && u.schoolId) return false;
    // School / county / school-type filters resolve through the user's school.
    if (userSchool && String(u.schoolId) !== String(userSchool)) return false;
    const sch = schoolById[u.schoolId];
    if (userCounty && (sch?.county || '') !== userCounty) return false;
    if (userSchoolType && (sch?.schoolType || '') !== userSchoolType) return false;
    const q = adminSearch.trim().toLowerCase();
    if (!q) return true;
    return (u.fullName || '').toLowerCase().includes(q) ||
           (u.email || '').toLowerCase().includes(q) ||
           (u.username || '').toLowerCase().includes(q) ||
           (schoolName(u.schoolId) || '').toLowerCase().includes(q);
  });

  const pagedAdmins = filteredAdmins.slice(adminPage * adminRows, (adminPage + 1) * adminRows);

  // ── Detail dialog content (reused for inline card) ────────────────────────
  const SchoolDetail = ({ s }) => s ? (
    <Box>
      {/* Identity */}
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Avatar sx={{ width: 52, height: 52, bgcolor: avatarColor(s.provisionStatus), fontSize: 22, fontWeight: 700 }}>
          {(s.name || '?').charAt(0).toUpperCase()}
        </Avatar>
        <Box flex={1}>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>{s.name}</Typography>
          <Typography variant="caption" color="text.secondary" fontFamily="monospace">{s.code}</Typography>
          {s.motto && <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic' }}>{s.motto}</Typography>}
        </Box>
        <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-end">
          <Chip label={s.provisionStatus} size="small" color={STATUS_COLOR[s.provisionStatus] || 'default'} />
          {s.status && s.status !== 'Active' && (
            <Chip label={s.status} size="small" color={s.status === 'Suspended' ? 'warning' : 'error'} variant="outlined" />
          )}
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Details grid */}
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5} mb={2}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>Type / Level</Typography>
          <Typography variant="body2">{[s.schoolType, s.level].filter(Boolean).join(' · ') || '—'}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>Subscription</Typography>
          <Typography variant="body2">{s.subscriptionTier || '—'}</Typography>
        </Box>
        {s.county && (
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}><LocationOn sx={{ fontSize: 12, mr: 0.3 }} />Location</Typography>
            <Typography variant="body2">{s.county}{s.subCounty ? `, ${s.subCounty}` : ''}</Typography>
          </Box>
        )}
        {s.phone && (
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}><Phone sx={{ fontSize: 12, mr: 0.3 }} />Phone</Typography>
            <Typography variant="body2">{s.phone}</Typography>
          </Box>
        )}
        {s.email && (
          <Box sx={{ gridColumn: 'span 2' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}><EmailIcon sx={{ fontSize: 12, mr: 0.3 }} />Email</Typography>
            <Typography variant="body2">{s.email}</Typography>
          </Box>
        )}
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600}><CalendarToday sx={{ fontSize: 12, mr: 0.3 }} />Registered</Typography>
          <Typography variant="body2">{fmtDate(s.createdAt)}</Typography>
        </Box>
        {s.provisionedAt && (
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Provisioned</Typography>
            <Typography variant="body2">{fmtDate(s.provisionedAt)}</Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Commitment + DB */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>Commitment Fee</Typography>
        {s.commitmentPaid
          ? <Chip icon={<CheckCircle />} label={`Paid ${fmtDate(s.commitmentPaidAt)}`} size="small" color="success" variant="outlined" />
          : <Chip label="Unpaid" size="small" color="warning" variant="outlined" />}
      </Box>
      {s.dedicatedDb && (
        <Box display="flex" alignItems="center" gap={0.8} mb={2}>
          <StorageRounded sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.disabled" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
            {s.dedicatedDb}
          </Typography>
        </Box>
      )}

      {/* Actions */}
      <Divider sx={{ mb: 2 }} />
      <Box display="flex" gap={1} flexWrap="wrap">
        {s.provisionStatus === 'Active' ? (
          <>
            <Button variant="contained" size="small" startIcon={<LoginIcon />} onClick={() => openSchoolInApp(s)} sx={{ flex: 1 }}>
              Open in App
            </Button>
            <Tooltip title="Re-run seed (adds missing sample data)">
              <Button variant="outlined" size="small"
                startIcon={provisioning === s._id ? <CircularProgress size={13} color="inherit" /> : <CloudSync />}
                disabled={provisioning === s._id} onClick={() => setReseedConfirm(s)}>
                Re-seed
              </Button>
            </Tooltip>
          </>
        ) : (
          <Button variant="contained" size="small"
            startIcon={provisioning === s._id ? <CircularProgress size={14} color="inherit" /> : <CloudSync />}
            disabled={provisioning === s._id} onClick={() => setReseedConfirm(s)} sx={{ flex: 1 }}>
            {provisioning === s._id ? 'Provisioning…' : 'Provision'}
          </Button>
        )}
        <Tooltip title="Edit details (name, contacts…)">
          <IconButton size="small" color="primary" onClick={() => openEditSchool(s)}><EditIcon fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title="Manage status">
          <IconButton size="small" color="primary" onClick={() => setManageDlg(s)}><Tune fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title="Delete school from registry">
          <IconButton size="small" color="error" onClick={() => setConfirmDel(s)}><Delete fontSize="small" /></IconButton>
        </Tooltip>
      </Box>
    </Box>
  ) : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f8', py: 3 }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3 } }}>

        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Back">
              <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: 'white', boxShadow: 1 }}>
                <ArrowBack />
              </IconButton>
            </Tooltip>
            <Apartment color="primary" sx={{ fontSize: 26, ml: 0.5 }} />
            <Box>
              <Typography variant="h5" fontWeight={700} color="primary">Dataport Admin</Typography>
              <Typography variant="caption" color="text.secondary">
                Schools registry — {schools.length} school{schools.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <Badge badgeContent={pendingInquiries} color="error">
              <Button
                variant="outlined"
                startIcon={<InboxIcon />}
                onClick={() => navigate('/admin/school-inquiries')}>
                School Inquiries
              </Button>
            </Badge>
            <Button
              variant="contained"
              startIcon={<School />}
              onClick={() => { setNewSchoolForm(NEW_SCHOOL_BLANK); setNewSchoolDlg(true); }}>
              Create New School
            </Button>
            <Button
              variant="contained"
              startIcon={creatingDemo ? <CircularProgress size={16} color="inherit" /> : <School />}
              onClick={createDemo} disabled={creatingDemo}
              sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
              {creatingDemo ? 'Creating…' : 'Create Demo School'}
            </Button>
            <Tooltip title="Refresh"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
            <Button variant="outlined" startIcon={<Logout />} onClick={signOut}>Sign Out</Button>
          </Box>
        </Box>

        {msg   && <Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert>}
        {error && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

        {/* Tabs */}
        <Box sx={{ bgcolor: 'white', borderRadius: '8px 8px 0 0', boxShadow: 1, mb: 0 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
            <Tab icon={<DashboardIcon fontSize="small" />} iconPosition="start"
              label="Overview" sx={{ textTransform: 'none', fontWeight: 700 }} />
            <Tab icon={<Apartment fontSize="small" />} iconPosition="start"
              label={`Schools (${schools.length})`} sx={{ textTransform: 'none', fontWeight: 700 }} />
            <Tab icon={<People fontSize="small" />} iconPosition="start"
              label={`Users (${allUsers.length})`} sx={{ textTransform: 'none', fontWeight: 700 }} />
            <Tab icon={<PaymentsIcon fontSize="small" />} iconPosition="start"
              label="Payments" sx={{ textTransform: 'none', fontWeight: 700 }} />
          </Tabs>
        </Box>
        <Divider />
        <Box sx={{ mb: 2 }} />

        {tab === 0 ? (
          /* ── Overview tab ── */
          loading ? (
            <Box textAlign="center" py={8}><CircularProgress /></Box>
          ) : (
            <Grid container spacing={2}>
              {[
                { label: 'Schools',          value: schools.length,                                          color: '#1565c0', icon: Apartment,          goTab: 1 },
                { label: 'Active Schools',   value: schools.filter(s => s.provisionStatus === 'Active').length, color: '#2e7d32', icon: CheckCircle,     goTab: 1 },
                { label: 'Awaiting Payment', value: schools.filter(s => !s.commitmentPaid).length,           color: '#e65100', icon: PaymentsIcon,       goTab: 3 },
                { label: 'Users',            value: allUsers.length,                                         color: '#0d47a1', icon: People,             goTab: 2 },
                { label: 'Super Admins',     value: allUsers.filter(u => u.isSuperAdmin).length,             color: '#c62828', icon: AdminPanelSettings, goTab: 2 },
                { label: 'Unassigned Users', value: allUsers.filter(u => !u.schoolId && !u.isSuperAdmin).length, color: '#6a1b9a', icon: WarningAmber,  goTab: 2 },
                { label: 'Pending Inquiries', value: pendingInquiries,                                          color: '#00838f', icon: InboxIcon,     to: '/admin/school-inquiries' },
              ].map(k => (
                <Grid item xs={6} sm={4} md={2} key={k.label}>
                  <Card elevation={2} onClick={() => k.to ? navigate(k.to) : setTab(k.goTab)}
                    sx={{ borderRadius: 3, cursor: 'pointer', borderTop: `4px solid ${k.color}`,
                      transition: 'transform .15s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 5 } }}>
                    <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                      <k.icon sx={{ fontSize: 26, color: k.color, mb: 0.5 }} />
                      <Typography variant="h4" fontWeight={800} color={k.color} lineHeight={1}>{k.value}</Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>{k.label}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  Click any card to jump to its section. Manage per-school data (permissions, groups,
                  finance, academics) by opening the school in the app from the Schools tab.
                </Alert>
              </Grid>
            </Grid>
          )
        ) : tab === 1 ? (
          /* ── Schools tab ── */
          <>
            <Box mb={2}>
              <TextField
                size="small" placeholder="Search schools…" value={search}
                onChange={e => { setSearch(e.target.value); setPage(0); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                sx={{ width: { xs: '100%', sm: 320 }, bgcolor: 'white', borderRadius: 1 }}
              />
            </Box>
            {loading ? (
              <Box textAlign="center" py={8}><CircularProgress /></Box>
            ) : filtered.length === 0 ? (
              <Box textAlign="center" py={8}>
                <Apartment sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">
                  {search ? 'No schools match your search.' : 'No schools yet. Create a demo school to get started.'}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: 1, overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700, py: 1.5 }}>School</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, ...hideXs }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, ...hideXs }}>County</TableCell>
                      <TableCell sx={{ fontWeight: 700, ...hideXs }}>Registered</TableCell>
                      <TableCell sx={{ fontWeight: 700, ...hideXs }}>Provisioned</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Fee</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Open</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.map(s => (
                      <TableRow
                        key={s._id} hover
                        onClick={() => setDetailDlg(s)}
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.50' } }}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar sx={{ width: 34, height: 34, fontSize: 15, fontWeight: 700, bgcolor: avatarColor(s.provisionStatus) + 'cc' }}>
                              {(s.name || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography fontWeight={600}>{s.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{s.code}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell><Chip label={s.provisionStatus} size="small" color={STATUS_COLOR[s.provisionStatus]} /></TableCell>
                        <TableCell sx={hideXs}><Typography variant="caption">{s.schoolType || '—'}</Typography></TableCell>
                        <TableCell sx={hideXs}><Typography variant="caption">{s.county || '—'}</Typography></TableCell>
                        <TableCell sx={hideXs}><Typography variant="caption">{fmtDate(s.createdAt)}</Typography></TableCell>
                        <TableCell sx={hideXs}><Typography variant="caption">{fmtDate(s.provisionedAt)}</Typography></TableCell>
                        <TableCell>
                          <Chip
                            label={s.commitmentPaid ? 'Paid' : 'Unpaid'}
                            size="small"
                            color={s.commitmentPaid ? 'success' : 'warning'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={s.provisionStatus === 'Active' ? 'Open in app' : 'Only active schools can be opened'}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={s.provisionStatus !== 'Active'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`${window.location.origin}?impersonate=${s._id}`, '_blank');
                                }}
                              >
                                <OpenInNew fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={filtered.length}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                  rowsPerPageOptions={[10, 25, 50]}
                />
              </Box>
            )}
          </>
        ) : tab === 2 ? (
          /* ── Users tab (all platform users) ── */
          <>
            <Box mb={2} display="flex" gap={1.5} flexWrap="wrap">
              <TextField
                size="small" placeholder="Search users or school…" value={adminSearch}
                onChange={e => { setAdminSearch(e.target.value); setAdminPage(0); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                sx={{ width: { xs: '100%', sm: 300 }, bgcolor: 'white', borderRadius: 1 }}
              />
              <TextField select size="small" label="Filter" value={userFilter}
                onChange={e => { setUserFilter(e.target.value); setAdminPage(0); }}
                sx={{ minWidth: 180, bgcolor: 'white', borderRadius: 1 }}>
                <MenuItem value="all">All users ({allUsers.length})</MenuItem>
                <MenuItem value="super">Super Admins ({allUsers.filter(u => u.isSuperAdmin).length})</MenuItem>
                <MenuItem value="admin">School Admins ({allUsers.filter(u => u.role === 'admin').length})</MenuItem>
                <MenuItem value="user">Staff ({allUsers.filter(u => u.role === 'user').length})</MenuItem>
                <MenuItem value="parent">Parents ({allUsers.filter(u => u.role === 'parent').length})</MenuItem>
                <MenuItem value="unassigned">No school ({allUsers.filter(u => !u.schoolId).length})</MenuItem>
              </TextField>
              <TextField select size="small" label="School" value={userSchool}
                onChange={e => { setUserSchool(e.target.value); setAdminPage(0); }}
                sx={{ minWidth: 200, bgcolor: 'white', borderRadius: 1 }}>
                <MenuItem value=""><em>All schools</em></MenuItem>
                {schools.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map(s => <MenuItem key={s._id} value={s._id}>{s.name} ({s.code})</MenuItem>)}
              </TextField>
              <TextField select size="small" label="County" value={userCounty}
                onChange={e => { setUserCounty(e.target.value); setAdminPage(0); }}
                sx={{ minWidth: 150, bgcolor: 'white', borderRadius: 1 }}>
                <MenuItem value=""><em>All counties</em></MenuItem>
                {counties.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Type" value={userSchoolType}
                onChange={e => { setUserSchoolType(e.target.value); setAdminPage(0); }}
                sx={{ minWidth: 130, bgcolor: 'white', borderRadius: 1 }}>
                <MenuItem value=""><em>All types</em></MenuItem>
                {schoolTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              {(userFilter !== 'all' || userSchool || userCounty || userSchoolType || adminSearch) && (
                <Button size="small" onClick={() => {
                  setUserFilter('all'); setUserSchool(''); setUserCounty(''); setUserSchoolType('');
                  setAdminSearch(''); setAdminPage(0);
                }} sx={{ textTransform: 'none' }}>Clear</Button>
              )}
              <Chip size="small" variant="outlined" label={`${filteredAdmins.length} shown`}
                sx={{ alignSelf: 'center', fontWeight: 700 }} />
              <Tooltip title={filteredAdmins.length ? 'Email the EduMIS promo to the users listed below' : 'No users to send to'}>
                <span style={{ marginLeft: 'auto' }}>
                  <Button
                    variant="outlined" color="secondary" startIcon={<EmailIcon />}
                    disabled={!filteredAdmins.length}
                    onClick={() => openPromo(filteredAdmins)}
                    sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Send Promotional Message
                  </Button>
                </span>
              </Tooltip>
            </Box>
            {loading ? (
              <Box textAlign="center" py={8}><CircularProgress /></Box>
            ) : filteredAdmins.length === 0 ? (
              <Box textAlign="center" py={8}>
                <AdminPanelSettings sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">
                  {adminSearch ? 'No users match your search.' : 'No users found.'}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: 1, overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700, py: 1.5 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 700, ...hideXs }}>School</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, ...hideXs }}>Last Login</TableCell>
                      <TableCell sx={{ fontWeight: 700, ...hideXs }}>Created</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedAdmins.map(u => (
                      <TableRow key={u._id} hover>
                        <TableCell>
                          {/* Click the name/avatar to open the user's edit popup */}
                          <Box display="flex" alignItems="center" gap={1.5}
                            onClick={() => openEditUser(u)}
                            sx={{ cursor: 'pointer', borderRadius: 1, p: 0.5, m: -0.5,
                              '&:hover': { bgcolor: 'action.hover' } }}>
                            <Avatar sx={{
                              width: 34, height: 34, fontSize: 15, fontWeight: 700,
                              bgcolor: u.isSuperAdmin ? '#c62828' : u.role === 'admin' ? '#e65100' : u.role === 'parent' ? '#00695c' : '#1565c0',
                            }}>
                              {(u.fullName || u.email || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={600} lineHeight={1.2}
                                sx={{ color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}>
                                {u.fullName || u.username || '—'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            <Chip size="small" variant="outlined"
                              label={u.role === 'admin' ? 'Admin' : u.role === 'parent' ? 'Parent' : 'Staff'}
                              color={u.role === 'admin' ? 'warning' : u.role === 'parent' ? 'info' : 'primary'}
                              sx={{ height: 20, fontSize: 11 }} />
                            {u.isSuperAdmin && (
                              <Chip label="Super Admin" size="small" color="error" sx={{ height: 20, fontSize: 11 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={hideXs}>
                          {u.schoolId
                            ? <Typography variant="caption">{schoolName(u.schoolId)}</Typography>
                            : <Chip label="No school" size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
                        </TableCell>
                        <TableCell>
                          <Chip label={u.status} size="small"
                            color={u.status === 'Active' ? 'success' : u.status === 'Blocked' ? 'error' : 'default'} />
                        </TableCell>
                        <TableCell sx={hideXs}><Typography variant="caption">{fmtDate(u.lastLogin)}</Typography></TableCell>
                        <TableCell sx={hideXs}><Typography variant="caption">{fmtDate(u.createdAt)}</Typography></TableCell>
                        <TableCell align="center">
                          <Box display="flex" gap={0.5} justifyContent="center">
                            <Tooltip title="Edit user">
                              <IconButton size="small" color="primary" onClick={() => openEditUser(u)}>
                                <Tune fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete user">
                              <IconButton size="small" color="error" onClick={() => setDeleteDlg(u)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={filteredAdmins.length}
                  page={adminPage}
                  onPageChange={(_, p) => setAdminPage(p)}
                  rowsPerPage={adminRows}
                  onRowsPerPageChange={e => { setAdminRows(parseInt(e.target.value, 10)); setAdminPage(0); }}
                  rowsPerPageOptions={[10, 25, 50]}
                />
              </Box>
            )}
            <Box mt={2}>
              <Button variant="contained" startIcon={<People />} onClick={() => {
                setEditForm({ role: 'user', status: 'Active', isSuperAdmin: false });
                setEditUserDlg({});
              }} sx={{ textTransform: 'none' }}>
                Add New User
              </Button>
            </Box>
          </>
        ) : (
          /* ── Payments tab (commitment fees & subscriptions) ── */
          loading ? (
            <Box textAlign="center" py={8}><CircularProgress /></Box>
          ) : (
            <Box sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: 1, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 700, py: 1.5 }}>School</TableCell>
                    <TableCell sx={{ fontWeight: 700, ...hideXs }}>Tier</TableCell>
                    <TableCell sx={{ fontWeight: 700, ...hideXs }}>Trial Ends</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Commitment Fee</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Provision</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schools.map(s => (
                    <TableRow key={s._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                        <Typography variant="caption" color="text.secondary" fontFamily="monospace">{s.code}</Typography>
                      </TableCell>
                      <TableCell sx={hideXs}>
                        <Chip label={s.subscriptionTier || 'Trial'} size="small"
                          color={s.subscriptionTier === 'Premium' ? 'secondary' : s.subscriptionTier === 'Private' ? 'primary' : 'default'}
                          variant="outlined" />
                      </TableCell>
                      <TableCell sx={hideXs}><Typography variant="caption">{fmtDate(s.trialEndsAt)}</Typography></TableCell>
                      <TableCell>
                        {s.commitmentPaid
                          ? <Chip icon={<CheckCircle />} label={`Paid ${fmtDate(s.commitmentPaidAt)}`} size="small" color="success" variant="outlined" />
                          : <Chip label="Unpaid" size="small" color="warning" />}
                      </TableCell>
                      <TableCell>
                        <Chip label={s.provisionStatus} size="small" color={STATUS_COLOR[s.provisionStatus] || 'default'} variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        {!s.commitmentPaid ? (
                          <Button size="small" variant="contained" color="warning" disabled={updating}
                            onClick={() => markPaid(s)} sx={{ textTransform: 'none', fontWeight: 700 }}>
                            Mark Paid
                          </Button>
                        ) : (
                          <Tooltip title="Change subscription tier">
                            <IconButton size="small" color="primary" onClick={() => setManageDlg(s)}>
                              <Tune fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )
        )}
      </Box>
      {/* School detail popup */}
      <Dialog open={Boolean(detailDlg)} onClose={() => setDetailDlg(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <IconButton size="small" onClick={() => setDetailDlg(null)} sx={{ mr: 0.5 }}>
            <ArrowBack fontSize="small" />
          </IconButton>
          <Apartment color="primary" fontSize="small" />
          <Typography fontWeight={700} flex={1}>School Details</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <SchoolDetail s={detailDlg} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDlg(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Manage status dialog */}
      <Dialog open={Boolean(manageDlg)} onClose={() => setManageDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tune color="primary" /> Manage — {manageDlg?.name}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>PROVISION STATUS</Typography>
          <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
            {['PendingPayment','Provisioning','Active','Suspended','Closed'].map(v => (
              <Chip key={v} label={v} size="small"
                color={STATUS_COLOR[v] || 'default'}
                variant={manageDlg?.provisionStatus === v ? 'filled' : 'outlined'}
                onClick={() => updateField(manageDlg, { provisionStatus: v })}
                disabled={updating || manageDlg?.provisionStatus === v}
                sx={{ cursor: 'pointer' }} />
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>ACCOUNT STATUS</Typography>
          <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
            {['Active','Suspended','Closed'].map(v => (
              <Chip key={v} label={v} size="small"
                color={v === 'Active' ? 'success' : v === 'Suspended' ? 'warning' : 'error'}
                variant={manageDlg?.status === v ? 'filled' : 'outlined'}
                onClick={() => updateField(manageDlg, { status: v })}
                disabled={updating || manageDlg?.status === v}
                sx={{ cursor: 'pointer' }} />
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>SUBSCRIPTION TIER</Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {['Trial','Community','Private','Premium'].map(v => (
              <Chip key={v} label={v} size="small"
                color={v === 'Premium' ? 'secondary' : v === 'Private' ? 'primary' : 'default'}
                variant={manageDlg?.subscriptionTier === v ? 'filled' : 'outlined'}
                onClick={() => updateField(manageDlg, { subscriptionTier: v })}
                disabled={updating || manageDlg?.subscriptionTier === v}
                sx={{ cursor: 'pointer' }} />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageDlg(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit school details dialog */}
      {schoolGuard.discardDialog}
      {/* ── Create New School ── */}
      <Dialog open={newSchoolDlg} onClose={() => !creatingNew && setNewSchoolDlg(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <School color="primary" /> Create New School
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Creates a fully-provisioned school with its own database (named from the code, e.g.
            <strong> {`cbems_${(newSchoolForm.code || 'code').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'code'}`}</strong>)
            and the baseline setup (chart of accounts, number series, banks, etc.).
          </Alert>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={5}>
              <TextField size="small" label="School Code" fullWidth required autoFocus
                value={newSchoolForm.code}
                onChange={e => setNewSchoolForm({ ...newSchoolForm, code: e.target.value.toUpperCase() })}
                helperText="Unique code / number" />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField size="small" label="School Name" fullWidth required
                value={newSchoolForm.name}
                onChange={e => setNewSchoolForm({ ...newSchoolForm, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField size="small" label="Motto" fullWidth
                value={newSchoolForm.motto}
                onChange={e => setNewSchoolForm({ ...newSchoolForm, motto: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField select size="small" label="Type" fullWidth value={newSchoolForm.schoolType}
                onChange={e => setNewSchoolForm({ ...newSchoolForm, schoolType: e.target.value })}>
                {['Public', 'Private', 'Community', 'Other'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select size="small" label="Level" fullWidth value={newSchoolForm.level}
                onChange={e => setNewSchoolForm({ ...newSchoolForm, level: e.target.value })}>
                {['Pre-Primary', 'Primary', 'Junior Secondary', 'Mixed'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField size="small" label="County" fullWidth value={newSchoolForm.county}
                onChange={e => setNewSchoolForm({ ...newSchoolForm, county: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField size="small" label="Sub-County" fullWidth value={newSchoolForm.subCounty}
                onChange={e => setNewSchoolForm({ ...newSchoolForm, subCounty: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField size="small" label="Phone" fullWidth value={newSchoolForm.phone}
                onChange={e => setNewSchoolForm({ ...newSchoolForm, phone: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField size="small" label="Email" type="email" fullWidth value={newSchoolForm.email}
                onChange={e => setNewSchoolForm({ ...newSchoolForm, email: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField size="small" label="Address" fullWidth value={newSchoolForm.address}
                onChange={e => setNewSchoolForm({ ...newSchoolForm, address: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewSchoolDlg(false)} disabled={creatingNew}>Cancel</Button>
          <Button variant="contained" onClick={createNewSchool} disabled={creatingNew}
            startIcon={creatingNew ? <CircularProgress size={16} color="inherit" /> : <School />}>
            {creatingNew ? 'Creating…' : 'Create & Provision'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editSchoolDlg)} onClose={schoolGuard.attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="primary" /> Edit School — {editSchoolDlg?.code}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={5}>
              <TextField size="small" label="School Code" fullWidth required
                value={editSchoolForm.code || ''}
                onChange={e => setEditSchoolForm({ ...editSchoolForm, code: e.target.value.toUpperCase() })}
                helperText="Unique code / number" />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField size="small" label="School Name" fullWidth required
                value={editSchoolForm.name || ''}
                onChange={e => setEditSchoolForm({ ...editSchoolForm, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField size="small" label="Motto" fullWidth
                value={editSchoolForm.motto || ''}
                onChange={e => setEditSchoolForm({ ...editSchoolForm, motto: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField select size="small" label="Type" fullWidth
                value={editSchoolForm.schoolType || 'Private'}
                onChange={e => setEditSchoolForm({ ...editSchoolForm, schoolType: e.target.value })}>
                {['Public', 'Private', 'Community', 'Other'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select size="small" label="Level" fullWidth
                value={editSchoolForm.level || 'Mixed'}
                onChange={e => setEditSchoolForm({ ...editSchoolForm, level: e.target.value })}>
                {['Pre-Primary', 'Primary', 'Junior Secondary', 'Mixed'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField size="small" label="County" fullWidth
                value={editSchoolForm.county || ''}
                onChange={e => setEditSchoolForm({ ...editSchoolForm, county: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField size="small" label="Sub-County" fullWidth
                value={editSchoolForm.subCounty || ''}
                onChange={e => setEditSchoolForm({ ...editSchoolForm, subCounty: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField size="small" label="Phone" fullWidth
                value={editSchoolForm.phone || ''}
                onChange={e => setEditSchoolForm({ ...editSchoolForm, phone: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField size="small" label="Email" type="email" fullWidth
                value={editSchoolForm.email || ''}
                onChange={e => setEditSchoolForm({ ...editSchoolForm, email: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField size="small" label="Address" fullWidth
                value={editSchoolForm.address || ''}
                onChange={e => setEditSchoolForm({ ...editSchoolForm, address: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={schoolGuard.attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={saveEditSchool} disabled={updating}>
            {updating ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={Boolean(confirmDel)} onClose={() => setConfirmDel(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Delete School
        </DialogTitle>
        <DialogContent>
          <Typography>Remove <strong>{confirmDel?.name}</strong> from the registry?</Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            This deletes the registry entry only. The client database
            ({confirmDel?.dedicatedDb || 'not yet created'}) must be dropped from Atlas separately if needed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDel(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={deleteSchool}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      {userGuard.discardDialog}
      <Dialog open={Boolean(editUserDlg)} onClose={userGuard.attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editForm._id ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
            <TextField size="small" label="Full Name" fullWidth
              value={editForm.fullName || ''} onChange={e => setEditForm({ ...editForm, fullName: e.target.value })} />
            <TextField size="small" label="Email" fullWidth
              value={editForm.email || ''} onChange={e => setEditForm({ ...editForm, email: e.target.value })} />
            <TextField size="small" label="Username" fullWidth
              value={editForm.username || ''} onChange={e => setEditForm({ ...editForm, username: e.target.value })} />
            <TextField select size="small" label="Role" fullWidth
              value={editForm.role || 'user'} onChange={e => setEditForm({ ...editForm, role: e.target.value })}>
              <MenuItem value="user">Staff</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="parent">Parent</MenuItem>
            </TextField>
            <TextField select size="small" label="Status" fullWidth
              value={editForm.status || 'Active'} onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Blocked">Blocked</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </TextField>
            <TextField select size="small" label="School" fullWidth
              value={editForm.schoolId || ''} onChange={e => setEditForm({ ...editForm, schoolId: e.target.value || null })}>
              <MenuItem value="">None</MenuItem>
              {schools.map(s => <MenuItem key={s._id} value={s._id}>{s.name} ({s.code})</MenuItem>)}
            </TextField>
            <Box display="flex" gap={1} alignItems="center">
              <Chip
                label="Super Admin"
                onClick={() => setEditForm({ ...editForm, isSuperAdmin: !editForm.isSuperAdmin })}
                color={editForm.isSuperAdmin ? 'primary' : 'default'}
                variant={editForm.isSuperAdmin ? 'filled' : 'outlined'}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          {editForm._id && editForm.email && (
            <Tooltip title="Email the EduMIS promo to this user">
              <Button color="secondary" startIcon={<EmailIcon />}
                onClick={() => openPromo([editUserDlg])}
                sx={{ mr: 'auto', textTransform: 'none' }}>
                Send Promo
              </Button>
            </Tooltip>
          )}
          <Button onClick={userGuard.attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={saveEditUser} disabled={updating}>
            {updating ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation */}
      <Dialog open={Boolean(deleteDlg)} onClose={() => setDeleteDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Delete User
        </DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete <strong>{deleteDlg?.fullName || deleteDlg?.email}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            This cannot be undone. All associated records will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => deleteUser(deleteDlg._id)} disabled={updating}>
            {updating ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Promotional Message Confirmation */}
      <Dialog open={promoDlg} onClose={() => !sendingPromo && setPromoDlg(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon color="secondary" /> Send Promotional Message
        </DialogTitle>
        <DialogContent dividers>
          {sendingPromo && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress color="secondary" />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                Sending {promoRecipients.length === 1 ? 'message' : `${promoRecipients.length} messages`}… please keep this window open.
              </Typography>
            </Box>
          )}
          {promoResult ? (
            <Alert severity={promoResult.failed ? (promoResult.sent ? 'warning' : 'error') : 'success'}>
              Sent to <strong>{promoResult.sent}</strong> of {promoResult.total} recipient
              {promoResult.total === 1 ? '' : 's'}
              {promoResult.failed ? ` — ${promoResult.failed} failed.` : '.'}
              {promoResult.errors?.length > 0 && (
                <Box component="div" sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Reason{promoResult.errors.length === 1 ? '' : 's'}:
                  </Typography>
                  {promoResult.errors.slice(0, 5).map((f, i) => (
                    <Typography key={i} variant="caption" display="block" sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}>
                      {f.email}: {f.error}
                    </Typography>
                  ))}
                  {promoResult.errors.length > 5 && (
                    <Typography variant="caption" color="text.secondary">
                      …and {promoResult.errors.length - 5} more.
                    </Typography>
                  )}
                </Box>
              )}
            </Alert>
          ) : (
            <>
              <TextField
                select fullWidth size="small" label="Campaign"
                value={promoCampaign} onChange={e => setPromoCampaign(e.target.value)}
                sx={{ mb: 2 }}
                helperText={promoCampaigns.find(c => c.id === promoCampaign)?.description || ' '}>
                {promoCampaigns.length === 0 ? (
                  <MenuItem value="">Default campaign</MenuItem>
                ) : promoCampaigns.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
                ))}
              </TextField>
              {promoRecipients.length === 1 ? (
                <Typography variant="body2" mb={2}>
                  This emails the selected promo to{' '}
                  <strong>{promoRecipients[0].fullName || promoRecipients[0].email}</strong>
                  {promoRecipients[0].email ? ` (${promoRecipients[0].email})` : ''}.
                </Typography>
              ) : (
                <Typography variant="body2" mb={2}>
                  This emails the selected promo to the{' '}
                  <strong>{promoRecipients.length}</strong> user{promoRecipients.length === 1 ? '' : 's'} in
                  the list below (matching your current filter and search).
                </Typography>
              )}
              <Box sx={{ bgcolor: '#fff8e1', p: 2, borderRadius: 1, border: '1px solid #ffe082' }}>
                <Typography variant="caption" color="text.secondary">
                  {promoRecipients.length === 1
                    ? <>This is a <strong>real email to a real user</strong>. Users without an email address are skipped.</>
                    : <>These are <strong>real emails to real users</strong>. To send to a smaller group, cancel and narrow the filter/search first. Users without an email address are skipped.</>}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoDlg(false)} disabled={sendingPromo}>
            {promoResult ? 'Close' : 'Cancel'}
          </Button>
          {!promoResult && (
            <Button variant="contained" color="secondary" startIcon={<EmailIcon />}
              onClick={sendPromo} disabled={sendingPromo || !promoRecipients.length}>
              {sendingPromo ? 'Sending…' : promoRecipients.length === 1 ? 'Send' : `Send to ${promoRecipients.length}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Reseed Confirmation Dialog */}
      <Dialog open={Boolean(reseedConfirm)} onClose={() => setReseedConfirm(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudSync color="primary" />
          <Typography fontWeight={700} flex={1}>
            {reseedConfirm?.provisionStatus === 'Active' ? 'Re-seed Sample Data?' : 'Provision School?'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" mb={2}>
            {reseedConfirm?.provisionStatus === 'Active'
              ? `Re-seeding will add or refresh sample data for "${reseedConfirm?.name}".`
              : `Provisioning will set up the dedicated database for "${reseedConfirm?.name}" and load sample data.`}
          </Typography>
          <Box sx={{ bgcolor: '#f5f7fa', p: 2, borderRadius: 1, border: '1px solid #e0e3e8' }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={1}>
              {reseedConfirm?.provisionStatus === 'Active' ? 'Will add/refresh:' : 'This will create:'}
            </Typography>
            <ul style={{ margin: '8px 0', paddingLeft: '20px', fontSize: '13px', color: '#546e7a' }}>
              <li>GL Accounts & Banks</li>
              <li>Items & Suppliers</li>
              <li>Learners & Employees</li>
              <li>Classes & Learning Areas</li>
              <li>Fully-marked Term 1 Assessment</li>
            </ul>
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              ℹ️ You can delete this sample data anytime. Existing data will not be affected.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReseedConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleReseedConfirm} disabled={provisioning === reseedConfirm?._id}>
            {provisioning === reseedConfirm?._id ? 'Processing…' : 'Yes, Proceed'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchoolsAdmin;
